import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AnomalySeverity } from '@services/activity.service';
import { ALL_ANOMALY_FILTER, fromSelectValue, toSelectValue } from './anomalySeverity';

const SEVERITIES: AnomalySeverity[] = ['low', 'medium', 'high', 'critical'];

interface AnomalyFiltersProps {
  severity: '' | AnomalySeverity;
  resolved: '' | 'true' | 'false';
  total: number;
  onSeverityChange: (severity: '' | AnomalySeverity) => void;
  onResolvedChange: (resolved: '' | 'true' | 'false') => void;
}

const AnomalyFilters: React.FC<AnomalyFiltersProps> = ({
  severity,
  resolved,
  total,
  onSeverityChange,
  onResolvedChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="mb-4 p-4">
      <div className="flex items-center gap-4">
        <span className="revy-cjk text-sm leading-[1.6]">{t('anomalies.severity')}:</span>
        <Select
          value={toSelectValue(severity)}
          onValueChange={(next) => onSeverityChange(fromSelectValue(next) as '' | AnomalySeverity)}
        >
          <SelectTrigger aria-label={t('anomalies.severity')} className="w-auto min-w-[6rem] gap-2">
            <SelectValue />
          </SelectTrigger>
          {/* Portalled to <body>, so it carries the island class. */}
          <SelectContent className="revy-ui">
            <SelectItem value={ALL_ANOMALY_FILTER} className="revy-cjk">
              {t('common.all')}
            </SelectItem>
            {SEVERITIES.map((value) => (
              <SelectItem key={value} value={value} className="revy-cjk">
                {t(`anomalies.severityValues.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="revy-cjk text-sm leading-[1.6]">{t('anomalies.status')}:</span>
        <Select
          value={toSelectValue(resolved)}
          onValueChange={(next) =>
            onResolvedChange(fromSelectValue(next) as '' | 'true' | 'false')
          }
        >
          <SelectTrigger aria-label={t('anomalies.status')} className="w-auto min-w-[6rem] gap-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="revy-ui">
            <SelectItem value="false" className="revy-cjk">
              {t('anomalies.unresolved')}
            </SelectItem>
            <SelectItem value="true" className="revy-cjk">
              {t('anomalies.resolved')}
            </SelectItem>
            <SelectItem value={ALL_ANOMALY_FILTER} className="revy-cjk">
              {t('common.all')}
            </SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />
        <span className="revy-cjk revy-figure text-xs leading-[1.7] text-muted-foreground">
          {t('anomalies.totalCount', { total: total.toLocaleString() })}
        </span>
      </div>
    </Card>
  );
};

export default AnomalyFilters;
